import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download } from "lucide-react";
import { Button, Loader } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { TApiOperatingStatement, TApiOperatingStatementRow } from "@/apis/proda_finance/types";

const AMOUNT_FORMATTER = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
});

/** Accounting style: negatives in brackets, zero as a dash, null (no baseline) as an em dash. */
export function formatStatementAmount(value: string | null): string {
    if (value === null) return "—";
    const amount = Number(value);
    if (amount === 0) return "-";
    const formatted = AMOUNT_FORMATTER.format(Math.abs(amount));
    return amount < 0 ? `(${formatted})` : formatted;
}

function formatMonth(isoDate: string): string {
    return MONTH_FORMATTER.format(new Date(`${isoDate.slice(0, 10)}T00:00:00Z`));
}

function periodColumnCaption(statement: TApiOperatingStatement): string {
    if (statement.period_basis === "movement" && statement.previous_period !== null) {
        return `Movement since ${formatMonth(statement.previous_period)}`;
    }
    if (statement.period_basis === "fiscal_year_start") {
        return "First period of the year (= YTD)";
    }
    return "No previous TB to compare";
}

const ALL_PROPERTIES = "";

export type TOperatingStatementTabProps = {
    fileId: string;
    loadStatement: (
        fileId: string,
        propertyCode: string | undefined,
    ) => Promise<TApiOperatingStatement>;
    onDownload: (fileId: string, propertyCode: string | undefined) => Promise<void>;
    canDownload: boolean;
};

type TLoadState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; statement: TApiOperatingStatement };

/**
 * The Trial Balance shown as an operating statement: income-statement accounts
 * only, grouped by the master CoA's header/total rows, with a period column
 * (movement vs the previous TB) and a YTD column (the TB balance itself).
 */
export function OperatingStatementTab(props: TOperatingStatementTabProps): React.ReactElement {
    const { fileId, loadStatement, onDownload, canDownload } = props;
    const [propertyCode, setPropertyCode] = useState<string>(ALL_PROPERTIES);
    const [state, setState] = useState<TLoadState>({ status: "loading" });
    const [properties, setProperties] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading" });
        loadStatement(fileId, propertyCode === ALL_PROPERTIES ? undefined : propertyCode)
            .then((statement) => {
                if (cancelled) return;
                setState({ status: "ready", statement });
                // The option list comes from the whole file, so it stays stable
                // while one property is selected.
                setProperties(statement.properties);
            })
            .catch(() => {
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [fileId, propertyCode, loadStatement, reloadKey]);

    const handleDownload = useCallback(async () => {
        setIsDownloading(true);
        try {
            await onDownload(fileId, propertyCode === ALL_PROPERTIES ? undefined : propertyCode);
        } finally {
            setIsDownloading(false);
        }
    }, [fileId, propertyCode, onDownload]);

    return (
        <div className="space-y-4" data-testid="operating-statement-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <label htmlFor="operating-statement-property" className="text-sm font-medium">
                        Property
                    </label>
                    <select
                        id="operating-statement-property"
                        data-testid="operating-statement-property"
                        value={propertyCode}
                        onChange={(e) => setPropertyCode(e.target.value)}
                        className="px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value={ALL_PROPERTIES}>All properties</option>
                        {properties.map((code) => (
                            <option key={code} value={code}>
                                {code}
                            </option>
                        ))}
                    </select>
                </div>
                {canDownload && (
                    <Button
                        variant="outline"
                        data-testid="operating-statement-download"
                        onClick={() => void handleDownload()}
                        disabled={state.status !== "ready" || isDownloading}
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download Excel
                    </Button>
                )}
            </div>

            {state.status === "loading" ? (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            ) : state.status === "error" ? (
                <EmptyState
                    variant="error"
                    title="Couldn't build the operating statement"
                    description="The statement for this file could not be loaded."
                    action={{ label: "Try again", onClick: () => setReloadKey((k) => k + 1) }}
                />
            ) : (
                <StatementBody statement={state.statement} />
            )}
        </div>
    );
}

function StatementBody({ statement }: { statement: TApiOperatingStatement }): React.ReactElement {
    if (statement.rows.length === 0 && statement.unclassified_rows.length === 0) {
        return (
            <EmptyState
                title="No income-statement accounts"
                description="None of this Trial Balance's lines map to a P&L account in the Chart of Accounts."
            />
        );
    }
    const unclassifiedTotal = Number(statement.reconciliation.unclassified_total);

    return (
        <div className="space-y-3">
            {!statement.grouped_by_coa_headers && (
                <p className="text-sm text-muted-foreground">
                    The Chart of Accounts has no header/total rows for P&amp;L accounts, so accounts
                    are listed without sections.
                </p>
            )}
            {statement.unclassified_rows.length > 0 && (
                <div
                    className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
                    data-testid="operating-statement-unclassified-warning"
                >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        {statement.unclassified_rows.length} account(s) on this TB are not in the
                        Chart of Accounts, so they cannot be classified and are excluded from Net
                        Income (YTD {formatStatementAmount(String(unclassifiedTotal))}). They are
                        listed at the bottom.
                    </span>
                </div>
            )}
            <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm" data-testid="operating-statement-table">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="px-4 py-2 text-left font-medium">Account</th>
                            <th className="px-4 py-2 text-left font-medium">Code</th>
                            <th className="px-4 py-2 text-right font-medium">
                                <div>Period ({formatMonth(statement.period)})</div>
                                <div className="text-xs font-normal text-muted-foreground">
                                    {periodColumnCaption(statement)}
                                </div>
                            </th>
                            <th className="px-4 py-2 text-right font-medium">YTD</th>
                        </tr>
                    </thead>
                    <tbody>
                        {statement.rows.map((row, index) => (
                            <StatementRow key={`${row.kind}-${index}`} row={row} />
                        ))}
                        <tr
                            className="border-t-2 font-semibold"
                            data-testid="operating-statement-net-income"
                        >
                            <td className="px-4 py-2" colSpan={2}>
                                Net Income
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums underline decoration-double">
                                {formatStatementAmount(statement.net_income_period)}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums underline decoration-double">
                                {formatStatementAmount(statement.net_income_ytd)}
                            </td>
                        </tr>
                        {statement.unclassified_rows.length > 0 && (
                            <>
                                <tr>
                                    <td
                                        className="px-4 pt-6 pb-1 font-semibold italic text-muted-foreground"
                                        colSpan={4}
                                    >
                                        Unclassified (not in Net Income)
                                    </td>
                                </tr>
                                {statement.unclassified_rows.map((row, index) => (
                                    <StatementRow key={`unclassified-${index}`} row={row} muted />
                                ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatementRow({
    row,
    muted = false,
}: {
    row: TApiOperatingStatementRow;
    muted?: boolean;
}): React.ReactElement {
    const isHeader = row.kind === "header";
    const isTotal = row.kind === "subtotal" || row.kind === "total";
    return (
        <tr
            className={cn(
                isHeader && "font-semibold",
                isTotal && "font-semibold",
                row.kind === "total" && "bg-muted/30",
                muted && "text-muted-foreground",
            )}
            data-testid={`operating-statement-row-${row.kind}`}
        >
            <td className="px-4 py-1.5" style={{ paddingLeft: `${1 + row.depth * 1.25}rem` }}>
                {isHeader ? row.label.toUpperCase() : row.label}
            </td>
            <td className="px-4 py-1.5 font-mono text-xs text-muted-foreground">
                {row.account_code ?? ""}
            </td>
            <td className={cn("px-4 py-1.5 text-right tabular-nums", isTotal && "border-t")}>
                {isHeader ? "" : formatStatementAmount(row.period_amount)}
            </td>
            <td className={cn("px-4 py-1.5 text-right tabular-nums", isTotal && "border-t")}>
                {isHeader ? "" : formatStatementAmount(row.ytd_amount)}
            </td>
        </tr>
    );
}
